import { extendTheme } from '@chakra-ui/react';

import { inputTheme } from './multipart-theme/input';
import { radioTheme } from './multipart-theme/radio';
import { selectTheme } from './multipart-theme/select';

/**
 * Theme aligned with rapidash so this app looks consistent when embedded
 * as an iframe inside the dashboard shell. Source values mirror
 * `rapidash/src/theme/colors.ts` and the MUI theme used there.
 *
 * @see https://chakra-ui.com/docs/styled-system/customize-theme
 */
const FONT_FAMILY = `'Inter Variable', 'Inter', sans-serif`;
const TEXT_COLOR = '#515151'; // rapidash palette.text.primary
const BG_DEFAULT = '#F7F6FD'; // rapidash palette.background.default

export const theme = extendTheme({
  // Single source of truth for the app background, so sticky chrome (navbar,
  // page title, wizard footer) can match the body instead of hardcoding a
  // color that drifts from it.
  semanticTokens: {
    colors: {
      appBg: BG_DEFAULT,
    },
  },
  styles: {
    global: {
      body: {
        fontFamily: `${FONT_FAMILY} !important`,
        fontSize: '14px',
        fontWeight: 'normal',
        color: TEXT_COLOR,
        bg: 'appBg',
      },
      // RSuite (used by the date picker) injects `body { background-color:
      // var(--rs-body) !important }` (white) after Chakra once an RSuite view
      // has mounted, which clobbers the app background and leaves the sticky
      // chrome (appBg) looking like a different tone. Beat it with higher
      // specificity + !important so the background stays appBg across SPA
      // navigation. Same approach already used for fontFamily above.
      'body.chakra-ui-light, body.chakra-ui-dark': {
        background: 'var(--chakra-colors-appBg) !important',
      },
    },
  },
  fonts: {
    heading: FONT_FAMILY,
    body: FONT_FAMILY,
  },

  textStyles: {
    /** @see https://m2.material.io/design/typography/the-type-system.html#type-scale */
    h1: { fontSize: '29px', fontWeight: 'semibold', color: TEXT_COLOR },
    h2: { fontSize: '21px', fontWeight: 'semibold', color: TEXT_COLOR },
    h3: { fontSize: '16px', fontWeight: 'semibold', color: TEXT_COLOR },
    h4: { fontSize: '16px', fontWeight: 'normal', color: TEXT_COLOR },
    p: { fontSize: '14px', fontWeight: 'normal', color: TEXT_COLOR },
    cardHeader: { fontSize: '16px', fontWeight: 'semibold', color: TEXT_COLOR },
    cardNumber: { fontSize: '20px', fontWeight: 'normal', color: TEXT_COLOR },
    formLabel: { fontSize: '14px', fontWeight: 'semibold', color: TEXT_COLOR },
  },
  components: {
    Input: inputTheme,
    Select: selectTheme,
    Radio: radioTheme,
    Button: {
      baseStyle: {
        borderRadius: '12px', // matches rapidash MuiButton radius
        fontWeight: 'normal',
        textTransform: 'inherit',
      },
      variants: {
        primary: {
          bgColor: 'primary.500',
          textColor: 'white',
          _hover: { bgColor: 'primary.700' },
        },
        secondary: {
          bgColor: 'secondary.300',
          textColor: 'white',
        },
      },
      sizes: {
        md: { fontSize: 'sm', minH: '40px', px: '24px' },
      },
    },
    Card: {
      baseStyle: { border: '1px', borderColor: 'gray.200' },
    },
    Table: {
      defaultProps: { variant: 'striped', colorScheme: 'blackAlpha' },
    },
    FormLabel: {
      baseStyle: {
        fontSize: '14px',
        fontWeight: 'semibold',
        color: TEXT_COLOR,
      },
    },
    Textarea: {
      defaultProps: { size: 'sm', variant: 'outline' },
    },
  },
  // Palette mirrored from rapidash/src/theme/colors.ts
  colors: {
    primary: {
      50: '#DAD0FF',
      100: '#BCA8FD',
      200: '#A890FB',
      300: '#9378F8',
      400: '#7D60F5', // brand accent — same the previous hsm theme used
      500: '#673AB7',
      600: '#5E35B1',
      700: '#512DA8',
      800: '#4527A0',
      900: '#311B92',
    },
    secondary: {
      50: '#DCF3EE',
      100: '#AAE2D4',
      200: '#6FD0B8',
      300: '#1ABC9C',
      400: '#00AD87',
      500: '#009C74',
      600: '#008F68',
      700: '#007F58',
      800: '#006F4B',
      900: '#00522F',
    },
    error: {
      50: '#FBCCC2',
      100: '#FAA1A8',
      200: '#F97F8D',
      300: '#F95E7C',
      400: '#F33C6A',
      500: '#EB2963',
      600: '#DA2560',
      700: '#C4225B',
      800: '#AF1D57',
      900: '#8A174F',
    },
    warning: {
      50: '#FDF4E6',
      100: '#F8DE5F',
      200: '#F7D74E',
      300: '#F6D03C',
      400: '#F5C52B',
      500: '#F4B822',
      600: '#F3AC1E',
      700: '#F29A1C',
      800: '#F18A1A',
      900: '#EF6D17',
    },
    info: {
      50: '#E4F2FF',
      100: '#BEDEFF',
      200: '#94CBFF',
      300: '#68B6FF',
      400: '#4AA5FF',
      500: '#3895FF',
      600: '#3B86FF',
      700: '#3B73EA',
      800: '#3B61D7',
      900: '#3840B7',
    },
  },
});
